import { useState, useEffect, useRef } from 'react'
import { format, parseISO, isToday, isYesterday } from 'date-fns'
import { useMessages } from './hooks/useMessages'
import { useAuth } from '../../hooks/useAuth'
import { memberColors } from '../../styles/tokens'
import { getInitials, cn } from '../../lib/utils'
import { Spinner } from '../../components/ui/Spinner'

const Avatar = ({ profile, size = 7 }) => {
  const sizeClass = size === 7 ? 'w-7 h-7' : 'w-8 h-8'
  return (
    <div className={cn(sizeClass, 'rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0')}
      style={{ backgroundColor: memberColors[profile?.member_color ?? 'violet'] }}>
      {getInitials(profile?.display_name ?? '?')}
    </div>
  )
}

const dayLabel = (dateStr) => {
  const d = parseISO(dateStr)
  if (isToday(d))     return 'Today'
  if (isYesterday(d)) return 'Yesterday'
  return format(d, 'EEEE, MMMM d')
}

const groupByDay = (messages) => {
  const groups = []
  let current = null
  for (const msg of messages) {
    const day = msg.created_at.slice(0, 10)
    if (day !== current) {
      groups.push({ day, messages: [] })
      current = day
    }
    groups[groups.length - 1].messages.push(msg)
  }
  return groups
}

export const MessagesPage = () => {
  const { user } = useAuth()
  const { data: messages = [], isLoading, sendMessage, deleteMessage } = useMessages()
  const [draft,    setDraft]    = useState('')
  const [sending,  setSending]  = useState(false)
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const handleSend = async (e) => {
    e.preventDefault()
    const body = draft.trim()
    if (!body) return
    setSending(true)
    try {
      await sendMessage.mutateAsync(body)
      setDraft('')
    } finally { setSending(false) }
  }

  const groups = groupByDay(messages)

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border bg-surface flex-shrink-0">
        <h1 className="text-base font-bold text-text-primary">Family Chat</h1>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center"><Spinner /></div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-16 text-text-muted">
              <div className="text-5xl mb-3">💬</div>
              <p className="text-sm">No messages yet. Say hello!</p>
            </div>
          )}

          {groups.map(({ day, messages: dayMsgs }) => (
            <div key={day}>
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wide">{dayLabel(dayMsgs[0].created_at)}</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              <div className="space-y-2">
                {dayMsgs.map((msg) => {
                  const isMe = msg.profiles?.id === user?.id
                  return (
                    <div key={msg.id} className={cn('flex gap-2 items-end', isMe && 'flex-row-reverse')}>
                      {!isMe && <Avatar profile={msg.profiles} />}
                      <div className={cn('group max-w-[75%] space-y-0.5', isMe && 'items-end flex flex-col')}>
                        {!isMe && (
                          <p className="text-[10px] font-medium text-text-muted ml-1">{msg.profiles?.display_name}</p>
                        )}
                        <div className="relative">
                          <div className={cn('px-3.5 py-2 rounded-2xl text-sm leading-relaxed',
                            isMe
                              ? 'bg-primary text-white rounded-br-sm'
                              : 'bg-surface border border-border text-text-primary rounded-bl-sm')}>
                            {msg.body}
                          </div>
                          {isMe && (
                            <button
                              onClick={() => deleteMessage.mutate(msg.id)}
                              className="absolute -left-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-text-muted hover:text-rose-500 p-0.5">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                              </svg>
                            </button>
                          )}
                        </div>
                        <p className={cn('text-[10px] text-text-muted', isMe ? 'text-right mr-1' : 'ml-1')}>
                          {format(parseISO(msg.created_at), 'h:mm a')}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      )}

      {/* Compose bar */}
      <div className="flex-shrink-0 bg-surface border-t border-border px-4 py-3 pb-[max(12px,env(safe-area-inset-bottom,12px))]">
        <form onSubmit={handleSend} className="flex gap-2 items-end">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e) } }}
            placeholder="Message your family…"
            rows={1}
            className="flex-1 rounded-2xl border border-border bg-background px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary max-h-32"
          />
          <button type="submit" disabled={sending || !draft.trim()}
            className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 disabled:opacity-40 hover:bg-primary/90 transition-colors">
            {sending
              ? <Spinner size="sm" />
              : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>}
          </button>
        </form>
      </div>
    </div>
  )
}
